import {DisabledRules, sItemKeyToInt, StockItem} from '../../../shared-utilities/models-old/datastructures';
import {StockFunctions} from '../../../shared-utilities/functions-old/stock-functions';
import {asyncScheduler, defer, Observable, of} from 'rxjs';
import {observeOn} from 'rxjs/operators';
import {IPaginationData} from '../../../shared/shared-models/pagination/pagination-data';
import {ISearchableFields} from '../../../shared/shared-models/type-sense/default-searchable-fields';
import {IStockManagerPreviewSnapshot} from '../models/stock-manager-preview-snapshot.interface';
import {getMonthlyAverage} from '../../../shared/shared-utils/calculations/calculations-stock';
import {getStockValueType} from '../../../shared-utilities/models-old/stock-stuff/stock-item-value-type';
import {HeaderMenuColumnData} from '../../../shared-utilities/models-old/ngp-report-grid/header-menu-data';

/**
 * Returns client-filter cache with the given store entry removed.
 */
export function clearClientFilteredStockItemsCacheForStore(
  cache: Record<string, StockItem[]>,
  storeId: string,
): Record<string, StockItem[]> {
  const nextCache = {...cache};
  delete nextCache[storeId];
  return nextCache;
}

/**
 * Updates the client-filter cache for a store when a full filtered result set is available.
 * An explicit empty array is stored so a zero-match client filter stays active.
 * `undefined` clears the store entry.
 */
export function setClientFilteredStockItemsCacheForStore(
  cache: Record<string, StockItem[]>,
  storeId: string,
  clientFilteredStockItemsCache?: StockItem[],
): Record<string, StockItem[]> {
  if (clientFilteredStockItemsCache === undefined) {
    return clearClientFilteredStockItemsCacheForStore(cache, storeId);
  }
  return {
    ...cache,
    [storeId]: clientFilteredStockItemsCache,
  };
}

/**
 * Adds an `originalValue` field to each item in the array, containing the original values of its properties.
 *
 * @template ObjectType - The type of objects in the array.
 *
 * @param {ObjectType[]} items - An array of objects to which the `originalValue` field will be added.
 *
 * @returns {(ObjectType & WithOriginalValue<ObjectType>)[]} An array of objects where each object has the `originalValue` field added.
 */
export function addOriginalValueFieldToItems<ObjectType>(items: ObjectType[]): (ObjectType & WithOriginalValue<ObjectType>)[] {
  return items.map((item: ObjectType): ObjectType & WithOriginalValue<ObjectType> => {
    const originalValue = {} as OriginalValue<ObjectType>;
    (Object.keys(item) as (keyof ObjectType)[]).forEach((property: keyof ObjectType): void => {
      originalValue[property] = {value: item[property] ?? 0} as unknown as OriginalValue<ObjectType>[keyof ObjectType];
    });

    originalValue['_tags'] = {value: item['_tags'] ?? []};
    originalValue['multipleSuppliers'] = {value: item['multipleSuppliers'] ?? []};
    originalValue['multipleBarcodes'] = {value: item['multipleBarcodes'] ?? []};
    return {...item, originalValue};
  });
}

/**
 * Interface representing an object that contains the original value of its properties.
 *
 * @interface WithOriginalValue
 * @template ValueType - The type of the value object whose properties will have original values.
 *
 * @property {OriginalValue<ValueType>} originalValue - The object containing original values for each property.
 */
interface WithOriginalValue<ValueType> {
  /** @member {OriginalValue<ValueType>} originalValue - The object containing original values for each property. */
  originalValue: OriginalValue<ValueType>;
}

/**
 * Type representing an object with original values for each of its properties.
 * Each property is wrapped in an object with a `value` field, and a `newlyAdded` field is also included.
 *
 * @template ObjectType - The type of the original object.
 *
 * @typedef {Object} OriginalValue
 * @property {{ value: ObjectType[Property] }} [Property] - Each property of the original object wrapped with a `value` field.
 * @property {{ value: boolean }} newlyAdded - Indicates whether the property was newly added.
 */
export type OriginalValue<ObjectType> = {
  [Property in keyof ObjectType]: { value: ObjectType[Property] };
} & {
  /** @member {{ value: boolean }} newlyAdded - A boolean indicating if the value was newly added. */
  newlyAdded: { value: boolean };
};

export const checkForDisabledItems = (stockItems: StockItem[], rulesDoc: DisabledRules, setOriginalValue?: boolean): StockItem[] => {

  stockItems.forEach((item: StockItem) => {
    item.disabled = StockFunctions.isDisabled(item, rulesDoc);
    if (setOriginalValue) {
      item.originalValue['disabled'] = {value: item.disabled};
    }
  });
  return stockItems;
};


export function yieldToLoop<T>(work: () => T): Observable<T> {
  return defer(() => of(work())).pipe(observeOn(asyncScheduler));
}

/**
 * Maps a Stock Manager column id to a Typesense sortable field.
 * Computed columns (e.g. monthly avg) fall back to their closest stored field.
 */
export function resolveStockManagerTypesenseSortField(colId: string): string | undefined {
  if (colId === 'unitsMonthAvg') {
    return sItemKeyToInt.unitsYear;
  }
  if (colId === 'depName' || colId === 'subDepName') {
    return undefined;
  }
  return sItemKeyToInt[colId as keyof typeof sItemKeyToInt];
}

/**
 * Client-side sort by computed monthly average units (Auto Ordering parity for loaded rows).
 */
export function sortStockItemsByMonthlyAverage(
  items: StockItem[],
  direction: 'asc' | 'desc',
): StockItem[] {
  const multiplier = direction === 'asc' ? 1 : -1;
  return [...items].sort((left: StockItem, right: StockItem): number => {
    const leftAvg = Number(getMonthlyAverage(left));
    const rightAvg = Number(getMonthlyAverage(right));
    if (leftAvg === rightAvg) {
      return 0;
    }
    return (leftAvg - rightAvg) * multiplier;
  });
}

/**
 * Client-side sort of stock items by a Stock Manager column id (used when Typesense sort is unavailable).
 */
export function sortStockItemsByColumn(
  items: StockItem[],
  colId: string,
  direction: 'asc' | 'desc',
): StockItem[] {
  if (colId === 'unitsMonthAvg') {
    return sortStockItemsByMonthlyAverage(items, direction);
  }
  if (colId === 'depName' || colId === 'subDepName') {
    return [...items];
  }

  const multiplier = direction === 'asc' ? 1 : -1;
  const valueType = getStockValueType(colId as never);

  return [...items].sort((left: StockItem, right: StockItem): number => {
    const leftRaw = left[colId as keyof StockItem];
    const rightRaw = right[colId as keyof StockItem];

    if (leftRaw == null && rightRaw == null) {
      return 0;
    }
    if (leftRaw == null) {
      return 1;
    }
    if (rightRaw == null) {
      return -1;
    }

    if (valueType === 'number' || valueType === 'boolean') {
      return (Number(leftRaw) - Number(rightRaw)) * multiplier;
    }

    if (valueType === 'date') {
      const leftMs = new Date(leftRaw as string | number | Date).getTime();
      const rightMs = new Date(rightRaw as string | number | Date).getTime();
      const safeLeft = Number.isNaN(leftMs) ? 0 : leftMs;
      const safeRight = Number.isNaN(rightMs) ? 0 : rightMs;
      return (safeLeft - safeRight) * multiplier;
    }

    return String(leftRaw).localeCompare(String(rightRaw), undefined, {sensitivity: 'base'}) * multiplier;
  });
}

/**
 * Returns display rows from the client-filtered cache for a column sort change.
 * The cache itself must stay in original fetch order so unsort restores that sequence.
 */
export function resolveClientFilteredStockItemsForSort(
  cacheItems: StockItem[],
  sortingEntry: [string, HeaderMenuColumnData] | null | undefined,
): StockItem[] {
  if (!sortingEntry) {
    return [...cacheItems];
  }

  const [colId, menuItem] = sortingEntry;
  if (menuItem.sortType !== 'sort-asc' && menuItem.sortType !== 'sort-desc') {
    return [...cacheItems];
  }

  const direction = menuItem.sortType === 'sort-asc' ? 'asc' : 'desc';
  return sortStockItemsByColumn(cacheItems, colId, direction);
}

/**
 * Restores stock manager query state saved before preview mode and clears the saved snapshot.
 */
export function restoreStockManagerStateAfterPreview(
  paginationData: Record<string, IPaginationData>,
  filters: Record<string, Record<string, ISearchableFields>>,
  previewSnapshotBeforePreview: Record<string, IStockManagerPreviewSnapshot>,
  storeId: string,
): {
  paginationData: Record<string, IPaginationData>;
  filters: Record<string, Record<string, ISearchableFields>>;
  previewSnapshotBeforePreview: Record<string, IStockManagerPreviewSnapshot>;
} {
  const snapshot = previewSnapshotBeforePreview[storeId];
  if (!snapshot) {
    return {paginationData, filters, previewSnapshotBeforePreview};
  }

  const {[storeId]: _removed, ...remainingSnapshots} = previewSnapshotBeforePreview;

  return {
    paginationData: {
      ...paginationData,
      [storeId]: {
        ...paginationData[storeId],
        pageSize: snapshot.pageSize,
        currentPage: snapshot.currentPage,
        searchedValue: snapshot.searchedValue,
      },
    },
    filters: {
      ...filters,
      [storeId]: snapshot.filters,
    },
    previewSnapshotBeforePreview: remainingSnapshots,
  };
}
